import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  editCrimeReferences,
  editInvestigation,
} from "services/incidentService";
import {
  editFurtherInvestigation,
  editReceivingInvestigationFile2,
  fetchFurtherInvestigation,
} from "services/prosecutorPoliceService";
import {
  CrimeReference,
  FurtherInvestigation,
  Investigation,
  InvestigationFile,
  Roles,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandler, errorHandlerOnForm, hasRole } from "utils/helper";
import FileUpload from "./FileUpload";

interface Props {
  title: string;
  message?: string;
  investigation: Investigation;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  setChangeTab: React.Dispatch<React.SetStateAction<string>>;
  onCancel: () => void;
  isOpen: boolean;
  incident?: CrimeReference;
  file?: InvestigationFile | null;
}

const FinishInvestigation: React.FC<Props> = ({
  title,
  message = "",
  onCancel,
  investigation,
  setRefetch,
  setChangeTab,
  isOpen,
  incident,
  file,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();

  const [formState, setFormState] = React.useState<any>({
    end_date: "",
    investigation_result_naration_file: null,
  });
  const [prosecutorRegistrationNumber, setProsecutorRegistrationNumber] =
    React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [furtherInvestigation, setFurtherInvestigation] =
    React.useState<FurtherInvestigation>();

  useEffect(() => {
    if (incident && isOpen) {
      setLoading(true);
      fetchFurtherInvestigation()
        ?.then((data) => {
          if (data) {
            setFurtherInvestigation(
              data?.find(
                (item) =>
                  item.incident_registration_number ===
                  incident?.Incident_Registration_number
              ) || undefined
            );
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [incident, isOpen]);

  if (!isOpen) return null;

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onCancel();
    }
  };

  const handleFile = (file: File | null) => {
    setFormState({ ...formState, investigation_result_naration_file: file });
  };

  const isClerk = hasRole(loginData?.roles, [Roles.clerk]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-[600px]">
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        {message && <p className="mt-2 text-sm text-gray-600">{message}</p>}
        <div className="flex flex-row mt-4 w-full items-baseline gap-5">
          <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
            End Date
          </label>
          <input
            type="date"
            className="flex-1 bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
            name="endDate"
            value={
              formState?.endDate
                ? new Date(formState.endDate).toISOString().split("T")[0]
                : ""
            }
            min={new Date().toISOString().split("T")[0]}
            onChange={(e) => {
              setFormState({
                ...formState,
                endDate: e.target.value,
              });
            }}
          />
        </div>
        {file && (
          <div className="flex flex-row mt-4 w-full items-baseline gap-5">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Prosecutor Registration Number
            </label>
            <input
              className="flex-1 bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              value={prosecutorRegistrationNumber}
              onChange={(e) => {
                if (!isNaN(Number(e.target.value))) {
                  setProsecutorRegistrationNumber(e.target.value);
                }
              }}
            />
          </div>
        )}
        {!investigation?.investigation_result_naration_file && (
          <div className="mt-4">
            <FileUpload
              label="Investigation Result Narrative File"
              fileTypes="*"
              info="Upload Files"
              required={false}
              onChange={handleFile}
            />
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`border border-[#E90101] bg-[#E90101] px-10 py-2 text-white 
                        font-[Poppins] cursor-pointer font-[500] 
                        text-base rounded-md hover:bg-white hover:text-[#E90101]
                        ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={loading || !formState?.endDate}
            onClick={() => {
              setLoading(true);

              editInvestigation({
                uu_id: investigation?.uu_id,
                end_date: formState?.endDate,
                investigation_result_naration_files:
                  formState?.investigation_result_naration_file || undefined,
              })
                .then(() => {
                  if (
                    (isClerk && incident) ||
                    (furtherInvestigation && incident)
                  ) {
                    editCrimeReferences(incident?.uu_id, {
                      // screening_team: incident?.screening_team_details?.map(
                      //   (i) => i?.uu_id
                      // ),
                      status: "forwarded",
                    });
                  }
                  if (isClerk && file) {
                    editReceivingInvestigationFile2({
                      id: file?.id,
                      status: "forward_to_coordinator",
                      // org_id: loginData?.org_unit,
                      // admin_unit_id: loginData?.admin_unit,
                      prosecutor_registration_number:
                        prosecutorRegistrationNumber,
                      assign_clerk: isClerk ? loginData?.full_name : undefined,
                    });
                  }
                  if (furtherInvestigation) {
                    editFurtherInvestigation({
                      ...furtherInvestigation,
                      decision: "back_to_registrar",
                    });
                  }

                  setLoading(false);
                  showTooltip("Successfully Finished!", "success");
                  setChangeTab("Investigation");
                  navigate("/investigation-files");
                  setRefetch((prev) => !prev);
                })
                .catch((error) => {
                  setLoading(false);
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandlerOnForm(error) ||
                      errorHandler(error) ||
                      "Something went wrong. Please try again.",
                    "error"
                  );
                });
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                {LoadingIcon}
                Loading...
              </div>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinishInvestigation;
